import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { fetchProducts, fetchCategories } from "../lib/storefront";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ALL_SIZES = ["XS", "S", "M", "L", "XL"];

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "newest", label: "Newest" },
  { id: "price-asc", label: "Price · Low to High" },
  { id: "price-desc", label: "Price · High to Low" },
  { id: "rating", label: "Top rated" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("cat") || "all";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState(initialCat);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [activeSizes, setActiveSizes] = useState([]);
  const [sort, setSort] = useState("featured");
  const [mobileFilters, setMobileFilters] = useState(false);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
    fetchCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  // Derive the universe of sizes from live product data — so if admin removes XS,
  // it disappears from the filter automatically.
  const SIZES = useMemo(() => {
    const present = new Set();
    products.forEach((p) => (p.sizes || []).forEach((s) => present.add(s)));
    return ALL_SIZES.filter((s) => present.has(s));
  }, [products]);

  // Auto-fit price slider once products load
  useEffect(() => {
    if (!products.length) return;
    const prices = products.map((p) => p.price);
    const min = Math.floor(Math.min(...prices) / 100) * 100;
    const max = Math.ceil(Math.max(...prices) / 100) * 100;
    setPriceRange([min, max]);
  }, [products.length]);

  useEffect(() => {
    if (cat === "all") {
      params.delete("cat");
    } else {
      params.set("cat", cat);
    }
    setParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat]);

  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const filtered = useMemo(() => {
    let list = [...products];
    if (cat !== "all") list = list.filter((p) => p.category === cat);
    list = list.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);
    if (activeSizes.length) list = list.filter((p) => p.sizes.some((s) => activeSizes.includes(s)));
    switch (sort) {
      case "price-asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        list.sort((a, b) => b.rating - a.rating);
        break;
      case "newest":
        list.sort((a, b) => (b.badge === "New" ? 1 : 0) - (a.badge === "New" ? 1 : 0));
        break;
      default:
        break;
    }
    return list;
  }, [products, cat, priceRange, activeSizes, sort]);

  const resetFilters = () => {
    setCat("all");
    if (products.length) {
      const prices = products.map((p) => p.price);
      setPriceRange([
        Math.floor(Math.min(...prices) / 100) * 100,
        Math.ceil(Math.max(...prices) / 100) * 100,
      ]);
    }
    setActiveSizes([]);
  };

  const FilterSidebar = () => (
    <aside className="space-y-10" data-testid="shop-filters">
      <div>
        <h4 className="font-body text-xs uppercase tracking-[0.24em] text-ink-500 mb-4">
          Category
        </h4>
        <div className="space-y-2">
          {[{ id: "all", name: "All Kurtis" }, ...categories].map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`filter-cat-${c.id}`}
              className={`block text-left font-body text-sm transition ${
                cat === c.id
                  ? "text-blush-600 font-medium"
                  : "text-ink-700 hover:text-blush-600"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-body text-xs uppercase tracking-[0.24em] text-ink-500 mb-4">
          Price range
        </h4>
        <Slider
          value={priceRange}
          min={0}
          max={Math.max(10000, ...products.map((p) => p.price))}
          step={100}
          onValueChange={setPriceRange}
          data-testid="filter-price"
          className="py-2"
        />
        <div className="mt-3 flex items-center justify-between text-xs font-body text-ink-700">
          <span>₹{priceRange[0].toLocaleString("en-IN")}</span>
          <span>₹{priceRange[1].toLocaleString("en-IN")}</span>
        </div>
      </div>

      <div>
        <h4 className="font-body text-xs uppercase tracking-[0.24em] text-ink-500 mb-4">
          Size
        </h4>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button
              key={s}
              onClick={() => toggle(activeSizes, setActiveSizes, s)}
              data-testid={`filter-size-${s}`}
              className={`h-9 w-9 rounded-full border text-xs font-body transition ${
                activeSizes.includes(s)
                  ? "bg-blush-600 text-white border-blush-600"
                  : "bg-white text-ink-700 border-blush-100 hover:border-blush-300"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={resetFilters}
        data-testid="filter-reset"
        className="text-xs font-body uppercase tracking-[0.22em] text-blush-600 hover:text-blush-700 transition"
      >
        Reset all filters
      </button>
    </aside>
  );

  return (
    <div data-testid="shop-page" className="pb-20">
      {/* HEADER — tighter */}
      <section className="relative py-10 md:py-14 bg-gradient-to-b from-blush-50 via-cream-100 to-cream-100 border-b border-blush-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="font-script text-2xl md:text-3xl text-blush-400">the edit</span>
          <h1 className="font-heading text-3xl md:text-5xl text-ink-900 mt-1">
            Shop all kurtis
          </h1>
          <p className="mt-3 font-body text-sm md:text-base text-ink-700 max-w-xl mx-auto">
            {filtered.length} pieces, thoughtfully cut, softly finished.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-8 md:pt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12">
        {/* Desktop sidebar */}
        <div className="hidden lg:block lg:col-span-3">
          <div className="sticky top-28">
            <FilterSidebar />
          </div>
        </div>

        <div className="lg:col-span-9">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4 mb-6">
            <button
              data-testid="shop-mobile-filter-btn"
              onClick={() => setMobileFilters(true)}
              className="lg:hidden inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blush-200 text-ink-700 font-body text-sm"
            >
              <SlidersHorizontal size={14} strokeWidth={1.5} />
              Filters
            </button>
            <p className="hidden lg:block font-body text-sm text-ink-500">
              Showing <span className="text-ink-900">{filtered.length}</span> of {products.length}
            </p>

            <div className="flex items-center gap-3">
              <span className="hidden md:inline text-xs uppercase tracking-[0.22em] text-ink-500 font-body">
                Sort by
              </span>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger
                  data-testid="shop-sort"
                  className="w-[200px] rounded-full bg-white border-blush-100 font-body"
                >
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-blush-100">
                  {SORTS.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="font-body">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="py-24 text-center border border-dashed border-blush-200 rounded-3xl">
              <p className="font-heading text-2xl text-ink-900">No matches, only maybes</p>
              <p className="mt-2 font-body text-ink-500">
                Try softening the filters to see more pieces.
              </p>
              <button onClick={resetFilters} className="btn-primary mt-6">
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Mobile drawer */}
      {mobileFilters && (
        <div
          className="fixed inset-0 z-[60] bg-ink-900/30 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileFilters(false)}
        >
          <div
            className="absolute inset-y-0 left-0 w-[85%] max-w-sm bg-cream-100 p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <h3 className="font-heading text-2xl text-ink-900">Filters</h3>
              <button
                onClick={() => setMobileFilters(false)}
                data-testid="shop-mobile-filter-close"
                className="h-9 w-9 rounded-full grid place-items-center text-ink-700 hover:bg-blush-50"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <FilterSidebar />
          </div>
        </div>
      )}
    </div>
  );
}
